import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  connect() {
    this.boundAnimateAfterStreamRender =
      this.animateAfterStreamRender.bind(this)
    this.boundSnapshotBeforeMorph = this.snapshotBeforeMorph.bind(this)
    this.boundAnimateAfterMorph = this.animateAfterMorph.bind(this)
    this.boundPreserveSlaTintClass = this.preserveSlaTintClass.bind(this)
    document.addEventListener(
      "turbo:before-stream-render",
      this.boundAnimateAfterStreamRender,
    )
    document.addEventListener(
      "turbo:before-stream-render",
      this.boundSnapshotBeforeMorph,
    )
    document.addEventListener(
      "turbo:morph-element",
      this.boundAnimateAfterMorph,
    )
    document.addEventListener(
      "turbo:before-morph-attribute",
      this.boundPreserveSlaTintClass,
    )
  }

  disconnect() {
    document.removeEventListener(
      "turbo:before-stream-render",
      this.boundAnimateAfterStreamRender,
    )
    document.removeEventListener(
      "turbo:before-stream-render",
      this.boundSnapshotBeforeMorph,
    )
    document.removeEventListener(
      "turbo:morph-element",
      this.boundAnimateAfterMorph,
    )
    document.removeEventListener(
      "turbo:before-morph-attribute",
      this.boundPreserveSlaTintClass,
    )
  }

  // Row-level broadcasts (big-grid index page): animate the replaced row
  // or the appended row. Skip morph-method streams — their animation is
  // handled via snapshotBeforeMorph / animateAfterMorph below.
  animateAfterStreamRender(event) {
    const streamElement = event.target
    const action = streamElement.getAttribute("action")
    if (action !== "replace" && action !== "append") return

    const targetId = streamElement.getAttribute("target")
    if (!this.element.querySelector(`#${targetId}`)) return

    if (streamElement.getAttribute("method") === "morph") return

    // Turbo 8 morphs elements in place after an async repaint cycle.
    // Chain two requestAnimationFrame calls to run after the morph.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const target = document.getElementById(targetId)
        if (!target) return

        const row = action === "append" ? target.lastElementChild : target
        if (row) {
          this.flashRow(row)
        }
      })
    })
  }

  // Before a morph-method stream renders, snapshot each row's
  // server-controlled fingerprint. The grid renders
  // data-row-fingerprint on every investigation row, derived from the
  // record's cache_key_with_version, so the value only changes when the
  // server-side investigation actually changes. Section headers and
  // detail rows don't carry the attribute and are skipped here.
  snapshotBeforeMorph(event) {
    const streamElement = event.target
    if (streamElement.getAttribute("method") !== "morph") return

    const targetId = streamElement.getAttribute("target")
    const target = this.element.querySelector(`#${targetId}`)
    if (!target) return

    this.previousRows = new Map(
      Array.from(target.querySelectorAll("tr[data-row-fingerprint][id]")).map((tr) => [
        tr.id,
        tr.dataset.rowFingerprint,
      ]),
    )
    this.morphTargetId = targetId
  }

  // After the morph completes on the snapshotted container, flash any
  // row whose fingerprint is new (id wasn't in the snapshot) or whose
  // fingerprint changed across the morph.
  animateAfterMorph(event) {
    if (!this.previousRows) return
    if (event.target.id !== this.morphTargetId) return

    this.#removeOrphanDetailRows(event.target)
    this.#restoreExpandedRowStyling(event.target)

    event.target.querySelectorAll("tr[data-row-fingerprint][id]").forEach((tr) => {
      const previousFingerprint = this.previousRows.get(tr.id)
      const isNew = previousFingerprint === undefined
      const isChanged = !isNew && previousFingerprint !== tr.dataset.rowFingerprint
      if (isNew || isChanged) {
        this.flashRow(tr)
      }
    })

    this.previousRows = null
    this.morphTargetId = null
  }

  // Detail rows are turbo-permanent so idiomorph preserves them across
  // tbody morphs even when the investigation row they belong to has been
  // removed. Sweep them out manually after each morph.
  #removeOrphanDetailRows(tbody) {
    tbody.querySelectorAll("tr.investigation-detail-row").forEach((detailRow) => {
      const match = detailRow.id.match(/^investigation_(\d+)_signals_row$/)
      if (!match) return

      const parentRow = tbody.querySelector(`#row_soc_investigation_${match[1]}`)
      if (!parentRow) detailRow.remove()
    })
  }

  // The expand button is turbo-permanent, so its aria-expanded survives
  // morphs. The parent <tr>, however, has its class attribute rewritten
  // by the morph — wiping out the expanded background classes the
  // row-expander controller had toggled on. Reapply them here.
  #restoreExpandedRowStyling(tbody) {
    tbody.querySelectorAll('button[aria-expanded="true"]').forEach((button) => {
      const controller = this.application.getControllerForElementAndIdentifier(
        button,
        "soc--investigation-row-expander",
      )
      controller?.applyExpandedState(true)
    })
  }

  // The SLA tint background classes on Created At cells are applied
  // client-side on a 1s tick by the elapsed-timer controller. The
  // server-rendered HTML never carries them, so without this hook
  // idiomorph would strip them on every Live Queue broadcast and the
  // next tick would re-add them — producing a visible flicker.
  preserveSlaTintClass(event) {
    if (event.detail.attributeName !== "class") return
    const target = event.target
    if (!(target instanceof Element)) return
    if (!target.hasAttribute("data-sla-cell-tint")) return
    if (!this.element.contains(target)) return
    event.preventDefault()
  }

  flashRow(row) {
    row.classList.add("row-updated")
    row.addEventListener(
      "animationend",
      () => row.classList.remove("row-updated"),
      { once: true },
    )
  }
}
